import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Address, Household, SyntheticHousehold } from '@guabi/shared';
import { Pill } from '@/components/ui/Pill';
import { GiftFields } from './GiftFields';
import { useUpdateHousehold } from './queries';

/**
 * A household's name, and the way to change it.
 *
 * Renaming is one click from the list rather than something inside a drawer,
 * because the names that need fixing were not typed on purpose — an import names
 * a household after whichever contact it saw first, and "Ana Maria Restrepo"
 * should read "Familia Restrepo". You find those by scanning the list, so the
 * fix belongs where you are looking.
 *
 * The name stays the disclosure control. A row that expands when you click its
 * name is the behaviour everyone already has, and taking it away to make room for
 * editing would cost more than the edit is worth — hence a separate pencil.
 *
 * **A synthetic household cannot be renamed and does not offer to be.** It is the
 * placeholder shown when the household item is missing but its people are not;
 * there is no row to write to, and a rename that silently did nothing would be
 * worse than no button.
 */
export function HouseholdName({
  wedId,
  household,
  count,
  open,
  onToggle,
}: {
  wedId: string;
  household: Household | SyntheticHousehold;
  count: number;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const rename = useUpdateHousehold(wedId);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(household.label);
  const input = useRef<HTMLInputElement>(null);

  const editable = !('synthetic' in household);

  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);

  function start() {
    setValue(household.label);
    setEditing(true);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const label = value.trim();
    // An empty name is not a rename, and neither is retyping the same one.
    if (!label || label === household.label || !('updatedAt' in household)) {
      setEditing(false);
      return;
    }
    rename.mutate(
      { householdId: household.householdId, input: { label, updatedAt: household.updatedAt } },
      { onSuccess: () => setEditing(false) },
    );
  }

  if (editing) {
    return (
      <form onSubmit={submit} className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <input
          ref={input}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          // Escape is what people reach for, and without it the only way out of
          // a mistyped name is to save it and edit again.
          onKeyDown={(e) => {
            if (e.key === 'Escape') setEditing(false);
          }}
          aria-label={t('guests.householdLabel')}
          maxLength={200}
          autoFocus
          className="min-h-tap min-w-0 flex-1 rounded border border-leaf bg-paper px-2 font-medium"
        />
        <button
          type="submit"
          disabled={rename.isPending}
          className="min-h-tap shrink-0 text-caption text-brass-text underline underline-offset-4"
        >
          {t('common.save')}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="min-h-tap shrink-0 text-caption text-leaf-soft underline underline-offset-4"
        >
          {t('common.cancel')}
        </button>
        {rename.isError && (
          <span role="alert" className="text-caption text-status-declinedFg">
            {t('guests.renameFailed')}
          </span>
        )}
      </form>
    );
  }

  return (
    <>
      <button
        type="button"
        // aria-expanded alone: the rows this reveals are siblings in a table, not
        // one element, so there is no single id for aria-controls to name — and
        // putting the same id on every member row would be four duplicate ids for
        // a family of four.
        aria-expanded={open}
        onClick={onToggle}
        className="-mx-2 inline-flex min-h-tap min-w-0 max-w-full items-center gap-2 rounded px-2 text-left hover:bg-cream"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 10 6"
          className={`h-1.5 w-2.5 shrink-0 text-leaf-soft transition-transform ${
            open ? '' : '-rotate-90'
          }`}
        >
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        <span className="min-w-0 truncate font-medium">{household.label}</span>
        <Pill>{t('guests.peopleCount', { count })}</Pill>
      </button>

      {editable && (
        <button
          type="button"
          onClick={start}
          // Named for the household it renames: a screen reader reading the page
          // otherwise hears "Rename" forty times with nothing to tell them apart.
          aria-label={t('guests.renameHouseholdNamed', { label: household.label })}
          title={t('guests.renameHousehold')}
          className="inline-flex min-h-tap shrink-0 items-center rounded px-1 text-leaf-soft hover:bg-cream hover:text-ink"
        >
          <svg aria-hidden="true" viewBox="0 0 16 16" className="size-3.5">
            <path
              d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </>
  );
}

/**
 * The household a guest belongs to: its name and the address on its envelope.
 *
 * Both lived only in the "add a family" drawer, so once a household existed
 * neither could be changed again — `useUpdateHousehold` was written and never
 * called. The address was the more surprising of the two to be missing, because
 * it is on the household rather than the person and so appears nowhere on the
 * guest form, which reads as though the app never asked for it.
 *
 * It is here, under the selector that says which household this person is in,
 * for two reasons: that selector is the only other place in the app that names a
 * household, and a household of one person draws no row in the list at all — so
 * for a guest list imported from contacts, where everyone is their own household,
 * this is the only way to reach either field.
 *
 * Closed by default. Most visits to this drawer are about the person, and three
 * address lines unfurled above the RSVP would push the common case off screen.
 */
export function HouseholdDetailsInline({
  wedId,
  household,
}: {
  wedId: string;
  household: Household | undefined;
}) {
  const { t } = useTranslation();
  const save = useUpdateHousehold(wedId);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState<Address>({});

  if (!household) return null;

  function start() {
    if (!household) return;
    setLabel(household.label);
    setAddress(household.address ?? {});
    setOpen(true);
  }

  function commit() {
    if (!household) return;
    const trimmed = label.trim();
    const cleaned = Object.fromEntries(
      Object.entries(address)
        .map(([k, v]) => [k, (v ?? '').trim()])
        .filter(([, v]) => v !== ''),
    );
    save.mutate(
      {
        householdId: household.householdId,
        input: {
          ...(trimmed && trimmed !== household.label ? { label: trimmed } : {}),
          address: cleaned,
          updatedAt: household.updatedAt,
        },
      },
      { onSuccess: () => setOpen(false) },
    );
  }

  const line = (key: keyof Address, labelKey: string) => (
    <label className="flex flex-col gap-1">
      <span className="text-caption text-leaf-soft">{t(labelKey)}</span>
      <input
        value={address[key] ?? ''}
        onChange={(e) => setAddress((prev) => ({ ...prev, [key]: e.target.value }))}
        // Not a <form>: this sits inside the guest drawer's own form, and a
        // nested one is invalid HTML — the browser drops it and Enter submits
        // the guest instead, saving the person and closing the drawer mid-edit.
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        className="min-h-tap rounded border border-line bg-paper px-2"
      />
    </label>
  );

  if (!open) {
    return (
      <button
        type="button"
        onClick={start}
        className="min-h-tap self-start text-caption text-brass-text underline underline-offset-4"
      >
        {t('guests.editHousehold')}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded border border-line bg-cream/40 p-3">
      <label className="flex flex-col gap-1">
        <span className="text-caption text-leaf-soft">{t('guests.householdLabel')}</span>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          maxLength={200}
          autoFocus
          className="min-h-tap rounded border border-line bg-paper px-2 font-medium"
        />
      </label>

      {/* One envelope goes to one address, which is why it is the household's
          and not the person's — said here so the guest form does not look as
          though it simply forgot to ask. */}
      <p className="text-caption text-leaf-soft">{t('guests.addressBelongsToHousehold')}</p>

      <GiftFields wedId={wedId} household={household} />

      {line('line1', 'guests.line1')}
      {line('line2', 'guests.line2')}
      <div className="grid grid-cols-2 gap-2">
        {line('city', 'guests.city')}
        {line('state', 'guests.state')}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {line('postalCode', 'guests.postalCode')}
        {line('country', 'guests.country')}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={commit}
          disabled={save.isPending}
          className="min-h-tap text-caption text-brass-text underline underline-offset-4"
        >
          {t('common.save')}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="min-h-tap text-caption text-leaf-soft underline underline-offset-4"
        >
          {t('common.cancel')}
        </button>
        {save.isError && (
          <span role="alert" className="text-caption text-status-declinedFg">
            {t('guests.renameFailed')}
          </span>
        )}
      </div>
    </div>
  );
}
